import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PersonIcon from "@mui/icons-material/Person";
import {
    Box,
    Card,
    CardContent,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Grid,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import ContractorRegistrationApiService from "../../../../infrastructure/api/contractorRegistration";

const ReviewStep = ({
  formData,
  setFormData,
  errors,
  setErrors,
  initialProvinces,
}) => {
  const [provinces, setProvinces] = useState(initialProvinces || []);
  const hasPrefetched = initialProvinces?.length;
  const [loading, setLoading] = useState(!hasPrefetched);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const needProvinces = !initialProvinces?.length;
        if (!needProvinces) {
          setLoading(false);
          return;
        }
        setLoading(true);
        const provincesRes = await ContractorRegistrationApiService.getProvinces();

        if (provincesRes?.isSuccess && provincesRes?.data) {
          setProvinces(provincesRes.data);
        }
      } catch (error) {
        console.error("خطا در دریافت اطلاعات:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getProvinceName = (provinceId) => {
    const province = provinces.find((p) => p.id === provinceId);
    return province ? province.title : "";
  };
  const handleTermsChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      agreedToTerms: event.target.checked,
    }));

    if (errors.agreedToTerms) {
      setErrors((prev) => ({
        ...prev,
        agreedToTerms: "",
      }));
    }
  };

  const getCompletionStatus = () => {
    const isLegal = formData.registrationType === "legal";
    
    const requiredFields = {
      // همیشه الزامی
      firstName: formData.firstName,
      lastName: formData.lastName,
      nationalCode: formData.nationalCode,
      mobile: formData.mobile,
      email: formData.email,
      birthDate: formData.birthDate,
      nationalCardImage: formData.nationalCardImage,
      workSamplesImages: formData.workSamplesImages?.length > 0,
    };

    // فقط برای حقوقی الزامی
    if (isLegal) {
      requiredFields.companyName = formData.companyName;
      requiredFields.companyRegistrationNumber = formData.companyRegistrationNumber;
      requiredFields.companyNationalId = formData.companyNationalId;
      requiredFields.companyPhone = formData.companyPhone;
      requiredFields.companyAddress = formData.companyAddress;
      requiredFields.cityId = formData.cityId;
      requiredFields.provinceId = formData.provinceId;
      requiredFields.postalCode = formData.postalCode;
      requiredFields.workExperience = formData.workExperience;
      requiredFields.companyLicenseImage = formData.companyLicenseImage;
      requiredFields.officialRegistrationDocumentsImage =
        formData.officialRegistrationDocumentsImage;
      requiredFields.authorizedSignatoriesImage =
        formData.authorizedSignatoriesImage;
      requiredFields.companyLogo = formData.companyLogo;
    } else {
      // برای حقیقی — آدرس و موقعیت برای صدور قرارداد الزامی است
      requiredFields.profileImage = formData.profileImage;
      requiredFields.companyAddress = formData.companyAddress;
      requiredFields.cityId = formData.cityId;
      requiredFields.provinceId = formData.provinceId;
      requiredFields.postalCode = formData.postalCode;
      requiredFields.workExperience = formData.workExperience;
    }

    // اطلاعات بانکی برای هر دو نوع (قرارداد)
    requiredFields.ibanNumber = formData.ibanNumber;
    requiredFields.bankAccountNumber = formData.bankAccountNumber;
    requiredFields.cardNumber = formData.cardNumber;

    const completedFields = Object.values(requiredFields).filter(
      (value) => value
    );
    const totalFields = Object.keys(requiredFields).length;

    return {
      completed: completedFields.length,
      total: totalFields,
      percentage: Math.round((completedFields.length / totalFields) * 100),
    };
  };

  const status = getCompletionStatus();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
        بررسی و تایید اطلاعات
      </Typography>

      {/* Completion Status */}
      <Card
        sx={{
          mb: 3,
          bgcolor:
            status.percentage === 100 ? "success.light" : "warning.light",
        }}
      >
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            {status.percentage === 100 ? (
              <CheckCircleIcon color="success" sx={{ mr: 1 }} />
            ) : (
              <ErrorIcon color="warning" sx={{ mr: 1 }} />
            )}
            <Typography variant="h6">
              وضعیت تکمیل: {status.completed} از {status.total} فیلد اجباری (
              {status.percentage}%)
            </Typography>
          </Box>
          {status.percentage < 100 && (
            <Typography variant="body2" color="text.secondary">
              لطفاً به مراحل قبل بازگشته و فیلدهای اجباری را تکمیل کنید.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Registration Type */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, bgcolor: "primary.lighter", borderLeft: "4px solid", borderColor: "primary.main" }}>
            <Typography variant="h6" gutterBottom>
              نوع ثبت‌نام
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formData.registrationType === "legal" ? "حقوقی (شرکت/سازمان)" : "حقیقی (شخصی)"}
            </Typography>
          </Paper>
        </Grid>

        {/* Personal Information Review */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <PersonIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">اطلاعات شخصی</Typography>
            </Box>
            <List dense>
              <ListItem>
                <ListItemText
                  primary={formData.registrationType === "legal" ? "نام و نام خانوادگی نماینده" : "نام و نام خانوادگی"}
                  secondary={`${formData.firstName} ${formData.lastName}`}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="کد ملی"
                  secondary={formData.nationalCode}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="شماره موبایل"
                  secondary={formData.mobile}
                />
              </ListItem>
              <ListItem>
                <ListItemText primary="ایمیل" secondary={formData.email} />
              </ListItem>
              {formData.birthDate && (
                <ListItem>
                  <ListItemText
                    primary="تاریخ تولد"
                    secondary={formData.birthDate.toLocaleDateString("fa-IR")}
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>

        {/* Company Information Review */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <BusinessIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">اطلاعات شرکت</Typography>
            </Box>
            {formData.registrationType === "individual" ? (
              <Box sx={{ p: 2, bgcolor: "info.lighter", borderRadius: 1 }}>
                <Typography variant="body2" color="info.main">
                  برای ثبت‌نام حقیقی (شخصی)، اطلاعات شرکت اختیاری است.
                </Typography>
              </Box>
            ) : (
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="نام شرکت"
                    secondary={formData.companyName}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="شماره ثبت"
                    secondary={formData.companyRegistrationNumber}
                  />
                </ListItem>
                {formData.companyNationalId && (
                  <ListItem>
                    <ListItemText
                      primary="شناسه ملی شرکت"
                      secondary={formData.companyNationalId}
                    />
                  </ListItem>
                )}
                <ListItem>
                  <ListItemText
                    primary="موقعیت"
                    secondary={`${formData.city}, ${getProvinceName(
                      formData.provinceId
                    )}`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="آدرس"
                    secondary={formData.companyAddress}
                  />
                </ListItem>
                {formData.workExperience && (
                  <ListItem>
                    <ListItemText
                      primary="سابقه کار"
                      secondary={`${formData.workExperience} سال`}
                    />
                  </ListItem>
                )}
              </List>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <AccountBalanceIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">اطلاعات بانکی</Typography>
            </Box>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="شماره شبا"
                  secondary={formData.ibanNumber || "—"}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="شماره حساب"
                  secondary={formData.bankAccountNumber || "—"}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="شماره کارت"
                  secondary={formData.cardNumber || "—"}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <CheckCircleIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">تأییدیه‌ها و عضویت‌ها</Typography>
            </Box>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  {formData.isSatbaApproved ? (
                    <CheckCircleIcon color="success" />
                  ) : (
                    <ErrorIcon color="disabled" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary="لیست پیمانکاران مورد تایید ساتبا"
                  secondary={formData.isSatbaApproved ? "بله" : "خیر"}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  {formData.isInDistributionVendorList ||
                  (formData.distributionCompanyIds || []).length > 0 ? (
                    <CheckCircleIcon color="success" />
                  ) : (
                    <ErrorIcon color="disabled" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary="وندور لیست شرکت‌های توزیع برق"
                  secondary={
                    (formData.distributionCompanyIds || []).length > 0
                      ? `${formData.distributionCompanyIds.length} شرکت انتخاب شده`
                      : formData.isInDistributionVendorList
                      ? "فعال — شرکتی انتخاب نشده"
                      : "خیر"
                  }
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Documents Review */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <AttachFileIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">مدارک آپلود شده</Typography>
            </Box>
            <List>
              <ListItem>
                <ListItemIcon>
                  {formData.nationalCardImage ? (
                    <CheckCircleIcon color="success" />
                  ) : (
                    <ErrorIcon color="error" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary="تصویر کارت ملی"
                  secondary={
                    formData.nationalCardImage
                      ? formData.nationalCardImage.name
                      : "آپلود نشده"
                  }
                />
              </ListItem>
              {formData.registrationType === "legal" && (
                <>
                  <ListItem>
                    <ListItemIcon>
                      {formData.companyLogo ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <ErrorIcon color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary="لوگوی شرکت"
                      secondary={
                        formData.companyLogo
                          ? formData.companyLogo.name
                          : "آپلود نشده"
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {formData.companyLicenseImage ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <ErrorIcon color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary="مجوز شرکت"
                      secondary={
                        formData.companyLicenseImage
                          ? formData.companyLicenseImage.name
                          : "آپلود نشده"
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {formData.officialRegistrationDocumentsImage ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <ErrorIcon color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary="مدارک ثبتی شرکت"
                      secondary={
                        formData.officialRegistrationDocumentsImage
                          ? formData.officialRegistrationDocumentsImage.name
                          : "آپلود نشده"
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {formData.authorizedSignatoriesImage ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <ErrorIcon color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary="مدارک صاحبان امضا"
                      secondary={
                        formData.authorizedSignatoriesImage
                          ? formData.authorizedSignatoriesImage.name
                          : "آپلود نشده"
                      }
                    />
                  </ListItem>
                </>
              )}
              {formData.registrationType === "individual" && (
                <ListItem>
                  <ListItemIcon>
                    {formData.profileImage ? (
                      <CheckCircleIcon color="success" />
                    ) : (
                      <ErrorIcon color="error" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="عکس پرسنلی"
                    secondary={
                      formData.profileImage
                        ? formData.profileImage.name
                        : "آپلود نشده"
                    }
                  />
                </ListItem>
              )}
              {formData.workSamplesImages &&
                formData.workSamplesImages.length > 0 && (
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary="نمونه کارها"
                      secondary={`${formData.workSamplesImages.length} فایل`}
                    />
                  </ListItem>
                )}
            </List>
          </Paper>
        </Grid>

        {/* Terms and Conditions */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              قوانین و مقررات
            </Typography>
            <Box
              sx={{
                maxHeight: 200,
                overflow: "auto",
                p: 2,
                bgcolor: "grey.50",
                borderRadius: 1,
                mb: 2,
              }}
            >
              <Typography variant="body2" paragraph>
                ۱. متقاضی متعهد می‌شود که تمامی اطلاعات ارائه شده صحیح و دقیق
                باشد.
              </Typography>
              <Typography variant="body2" paragraph>
                ۲. شرکت سولارنت حق بررسی و تایید یا رد درخواست را برای خود محفوظ
                می‌دارد.
              </Typography>
              <Typography variant="body2" paragraph>
                ۳. پیمانکار متعهد به رعایت استانداردهای فنی و ایمنی در اجرای
                پروژه‌ها می‌باشد.
              </Typography>
              <Typography variant="body2" paragraph>
                ۴. تمامی مدارک و اطلاعات ارائه شده محرمانه تلقی شده و فقط برای
                بررسی صلاحیت استفاده خواهد شد.
              </Typography>
              <Typography variant="body2" paragraph>
                ۵. در صورت تایید، قرارداد همکاری جداگانه‌ای منعقد خواهد شد.
              </Typography>
              <Typography variant="body2">
                ۶. پیمانکار متعهد به ارائه خدمات با کیفیت و در مهلت مقرر
                می‌باشد.
              </Typography>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.agreedToTerms}
                  onChange={handleTermsChange}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  قوانین و مقررات فوق را مطالعه کرده و با آنها موافقم *
                </Typography>
              }
            />
            {errors.agreedToTerms && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 1, display: "block" }}
              >
                {errors.agreedToTerms}
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2, bgcolor: "info.light" }}>
            <Typography variant="body2" color="white">
              <strong>مرحله بعدی:</strong> پس از ارسال درخواست، تیم بررسی
              سولارنت طی ۳ تا ۵ روز کاری درخواست شما را بررسی کرده و نتیجه را از
              طریق پیامک و ایمیل اطلاع‌رسانی خواهد کرد.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReviewStep;
